/*
 * SOYGlow Skin Analyzer
 * Archivo: skin-analyzer.js
 * Versión: 1.4.0
 *
 * Requisitos:
 * - skin-analyzer.liquid
 * - skin-analyzer.css
 * - skin-analyzer-data.js
 *
 * Carga este archivo DESPUÉS de skin-analyzer-data.js.
 */

(function (window, document) {
  'use strict';

  const ROOT_ID = 'soyglow-skin-analyzer';
  const TOTAL_STEPS = 6;
  const LOADING_TIME_MS = 1800;

  const DEFAULT_STEP_ORDER = [
    'Desmaquillantes',
    'Limpieza',
    'Exfoliantes',
    'Tónicos',
    'Mascarilla',
    'Serums',
    'Contorno de ojos',
    'Cremas',
    'Protector Solar'
  ];

  const STEP_ALIASES = {
    desmaquillante: 'Desmaquillantes',
    desmaquillantes: 'Desmaquillantes',
    limpieza: 'Limpieza',
    limpiador: 'Limpieza',
    limpiadores: 'Limpieza',
    exfoliante: 'Exfoliantes',
    exfoliantes: 'Exfoliantes',
    tonico: 'Tónicos',
    tonicos: 'Tónicos',
    mascarilla: 'Mascarilla',
    mascarillas: 'Mascarilla',
    serum: 'Serums',
    serums: 'Serums',
    contorno_de_ojos: 'Contorno de ojos',
    crema: 'Cremas',
    cremas: 'Cremas',
    protector_solar: 'Protector Solar',
    solares: 'Protector Solar'
  };

  const FALLBACK_SKIN_LABELS = {
    grasa: 'Piel grasa',
    mixta: 'Piel mixta',
    normal: 'Piel normal',
    seca: 'Piel seca',
    deshidratada: 'Piel deshidratada'
  };

  const FALLBACK_CONCERN_LABELS = {
    acne: 'Acné y brotes',
    puntos_negros: 'Puntos negros',
    manchas: 'Manchas e hiperpigmentación',
    arrugas: 'Arrugas y líneas de expresión',
    deshidratacion: 'Deshidratación',
    descamacion: 'Descamación y barrera alterada',
    flacidez: 'Pérdida de firmeza',
    sensibilidad: 'Sensibilidad',
    rojeces: 'Rojeces',
    pores: 'Poros visibles',
    sebum: 'Exceso de grasa',
    glow: 'Falta de luminosidad',
    hydration: 'Necesidad de hidratación',
    wrinkles: 'Prevención antiedad',
    spots: 'Prevención de manchas',
    firmness: 'Firmeza'
  };

  class SkinAnalyzer {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('#soyglow-analyzer-form');
      this.emailGate = root.querySelector('#soyglow-email-gate');
      this.emailInput = root.querySelector('#soyglow-email');
      this.marketingConsentInput = root.querySelector('#soyglow-marketing-consent');
      this.startButton = root.querySelector('#soyglow-start');
      this.gateError = root.querySelector('#soyglow-gate-error');
      this.marketingForm = root.querySelector('#soyglow-marketing-form');
      this.marketingEmail = root.querySelector('#soyglow-marketing-email');
      this.steps = Array.from(root.querySelectorAll('.soyglow-analyzer__step'));
      this.progress = root.querySelector('.soyglow-analyzer__progress');
      this.progressLabel = root.querySelector('#soyglow-progress-label');
      this.progressValue = root.querySelector('#soyglow-progress-value');
      this.progressBar = root.querySelector('#soyglow-progress-bar');

      this.prevButton = root.querySelector('#soyglow-prev');
      this.nextButton = root.querySelector('#soyglow-next');
      this.submitButton = root.querySelector('#soyglow-submit');
      this.restartButton = root.querySelector('#soyglow-restart');
      this.addRoutineButton = root.querySelector('#soyglow-add-routine');
      this.downloadPdfButton = root.querySelector('#soyglow-download-pdf');
      this.pdfMessage = root.querySelector('#soyglow-pdf-message');
      this.cartMessage = root.querySelector('#soyglow-cart-message');
      this.errorElement = root.querySelector('#soyglow-form-error');

      this.loadingSection = root.querySelector('#soyglow-analyzing');
      this.resultsSection = root.querySelector('#soyglow-results');

      this.resultTitle = root.querySelector('#soyglow-result-title');
      this.resultSummary = root.querySelector('#soyglow-result-summary');
      this.skinProfileElement = root.querySelector('#soyglow-skin-profile');
      this.concernsElement = root.querySelector('#soyglow-concerns');
      this.ingredientsElement = root.querySelector('#soyglow-ingredients');
      this.routineElement = root.querySelector('#soyglow-routine-list');
      this.explanationElement = root.querySelector('#soyglow-explanation');

      this.currentStep = 1;
      this.answers = this.createEmptyAnswers();
      this.data = this.getData();
      this.scores = this.createEmptyScores();
      this.analysis = null;
      this.user = { email: '', marketingConsent: false };
      this.emailEndpoint = String(root.dataset.emailEndpoint || '').trim();
      this.shopName = String(root.dataset.shopName || 'SOYGlow').trim();
      this.availabilityCache = new Map();

      this.init();
    }

    createEmptyAnswers() {
      return {
        age: null,
        skinFeel: null,
        pores: null,
        concerns: [],
        goal: null,
        routine: null
      };
    }

    createEmptyScores() {
      return {
        skinTypes: {},
        concerns: {},
        problems: {},
        objectives: {},
        ingredients: {}
      };
    }

    getData() {
      const modernData =
        window.SOYGlowSkinAnalyzer &&
        window.SOYGlowSkinAnalyzer.data;

      const legacyData = window.SOYGlowSkinAnalyzerData;
      const data = modernData || legacyData;

      if (!data) {
        throw new Error(
          'No se ha encontrado skin-analyzer-data.js. Comprueba que se carga antes de skin-analyzer.js.'
        );
      }

      return data;
    }

    getProducts() {
      if (this.data.catalog && Array.isArray(this.data.catalog.products)) {
        return this.data.catalog.products;
      }

      if (Array.isArray(this.data.products)) {
        return this.data.products;
      }

      return [];
    }

    getScoring() {
      return this.data.scoring || {};
    }

    getKnowledge() {
      return this.data.knowledge || {};
    }

    getConfig() {
      return this.data.config || {};
    }

    init() {
      if (!this.form || this.steps.length === 0) {
        return;
      }

      this.bindEvents();
      this.configureConcernCheckboxes();
      this.progress.hidden = true;
      this.form.hidden = true;
    }

    bindEvents() {
      this.startButton?.addEventListener('click', () => this.startAnalysis());
      this.emailInput?.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.startAnalysis();
        }
      });

      this.nextButton?.addEventListener('click', () => this.goNext());
      this.prevButton?.addEventListener('click', () => this.goPrevious());

      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });

      this.restartButton?.addEventListener('click', () => this.restart());

      this.addRoutineButton?.addEventListener('click', () => {
        this.addRoutineToCart();
      });

      this.downloadPdfButton?.addEventListener('click', () => {
        this.downloadAnalysisPdf();
      });

      this.form.addEventListener('change', (event) => {
        const input = event.target;

        if (!(input instanceof HTMLInputElement)) {
          return;
        }

        if (input.name === 'concerns') {
          this.handleConcernSelection(input);
        }

        this.clearError();
      });

      this.form.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter') {
          return;
        }

        const target = event.target;
        if (target instanceof HTMLInputElement && target.type === 'checkbox') {
          return;
        }

        event.preventDefault();

        if (this.currentStep === TOTAL_STEPS) {
          this.submit();
        } else {
          this.goNext();
        }
      });
    }

    startAnalysis() {
      const email = String(this.emailInput?.value || '').trim();

      if (!this.isValidEmail(email)) {
        this.showGateError('Introduce un correo electrónico válido para comenzar.');
        this.emailInput?.focus();
        return;
      }

      this.user = {
        email,
        marketingConsent: Boolean(this.marketingConsentInput?.checked)
      };

      this.clearGateError();

      if (this.user.marketingConsent) {
        this.subscribeToMarketing().catch((error) => {
          console.warn('[SOYGlow Marketing]', error);
        });
      }

      if (this.emailGate) this.emailGate.hidden = true;
      this.progress.hidden = false;
      this.form.hidden = false;
      this.showStep(1);
    }

    isValidEmail(email) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email || '').trim());
    }

    showGateError(message) {
      if (!this.gateError) return;
      this.gateError.textContent = message;
      this.gateError.hidden = false;
    }

    clearGateError() {
      if (!this.gateError) return;
      this.gateError.textContent = '';
      this.gateError.hidden = true;
    }

    async subscribeToMarketing() {
      if (!this.marketingForm || !this.marketingEmail || !this.user.email) return;

      this.marketingEmail.value = this.user.email;
      const formData = new FormData(this.marketingForm);

      const response = await fetch(this.marketingForm.action, {
        method: 'POST',
        body: formData,
        credentials: 'same-origin',
        headers: { 'Accept': 'text/html' }
      });

      if (!response.ok) {
        throw new Error('No se pudo registrar la suscripción de marketing.');
      }
    }

    configureConcernCheckboxes() {
      const checkboxes = Array.from(
        this.form.querySelectorAll('input[name="concerns"]')
      );

      checkboxes.forEach((checkbox) => {
        checkbox.addEventListener('change', () =>
          this.handleConcernSelection(checkbox)
        );
      });
    }

    handleConcernSelection(changedInput) {
      const checkboxes = Array.from(
        this.form.querySelectorAll('input[name="concerns"]')
      );
      const noneCheckbox = checkboxes.find(
        (checkbox) => checkbox.value === 'ninguna'
      );

      if (!noneCheckbox) {
        return;
      }

      if (changedInput.value === 'ninguna' && changedInput.checked) {
        checkboxes.forEach((checkbox) => {
          if (checkbox !== noneCheckbox) {
            checkbox.checked = false;
          }
        });
        return;
      }

      if (changedInput.value !== 'ninguna' && changedInput.checked) {
        noneCheckbox.checked = false;
      }
    }

    showStep(stepNumber) {
      this.currentStep = Math.min(
        Math.max(Number(stepNumber) || 1, 1),
        TOTAL_STEPS
      );

      this.steps.forEach((step) => {
        const isActive = Number(step.dataset.step) === this.currentStep;
        step.hidden = !isActive;
        step.classList.toggle('is-active', isActive);
      });

      this.updateProgress();
      this.updateNavigation();
      this.clearError();
      this.scrollToAnalyzer();
    }

    updateProgress() {
      const percentage = Math.round(
        (this.currentStep / TOTAL_STEPS) * 100
      );

      if (this.progressLabel) {
        this.progressLabel.textContent =
          `Paso ${this.currentStep} de ${TOTAL_STEPS}`;
      }

      if (this.progressValue) {
        this.progressValue.textContent = `${percentage}%`;
      }

      if (this.progressBar) {
        this.progressBar.style.width = `${percentage}%`;
      }
    }

    updateNavigation() {
      if (this.prevButton) {
        this.prevButton.hidden = this.currentStep === 1;
      }

      if (this.nextButton) {
        this.nextButton.hidden = this.currentStep === TOTAL_STEPS;
      }

      if (this.submitButton) {
        this.submitButton.hidden = this.currentStep !== TOTAL_STEPS;
      }
    }

    goNext() {
      if (!this.validateStep(this.currentStep)) {
        return;
      }

      this.captureStepAnswer(this.currentStep);

      if (this.currentStep < TOTAL_STEPS) {
        this.showStep(this.currentStep + 1);
      }
    }

    goPrevious() {
      this.captureStepAnswer(this.currentStep);

      if (this.currentStep > 1) {
        this.showStep(this.currentStep - 1);
      }
    }

    validateStep(stepNumber) {
      const step = this.steps.find(
        (item) => Number(item.dataset.step) === Number(stepNumber)
      );

      if (!step) {
        return false;
      }

      const question = step.dataset.question;
      const checkedInputs = Array.from(
        step.querySelectorAll('input:checked')
      );

      if (question === 'concerns') {
        if (checkedInputs.length === 0) {
          this.showError('Selecciona al menos una opción para continuar.');
          return false;
        }

        return true;
      }

      if (checkedInputs.length === 0) {
        this.showError('Selecciona una respuesta para continuar.');
        return false;
      }

      return true;
    }

    captureStepAnswer(stepNumber) {
      const step = this.steps.find(
        (item) => Number(item.dataset.step) === Number(stepNumber)
      );

      if (!step) {
        return;
      }

      const question = step.dataset.question;

      switch (question) {
        case 'age':
          this.answers.age =
            step.querySelector('input[name="age"]:checked')?.value || null;
          break;

        case 'skin_feel':
          this.answers.skinFeel =
            step.querySelector('input[name="skin_feel"]:checked')?.value ||
            null;
          break;

        case 'pores':
          this.answers.pores =
            step.querySelector('input[name="pores"]:checked')?.value || null;
          break;

        case 'concerns':
          this.answers.concerns = Array.from(
            step.querySelectorAll('input[name="concerns"]:checked')
          ).map((input) => input.value);
          break;

        case 'goal':
          this.answers.goal =
            step.querySelector('input[name="goal"]:checked')?.value || null;
          break;

        case 'routine':
          this.answers.routine =
            step.querySelector('input[name="routine"]:checked')?.value || null;
          break;

        default:
          break;
      }
    }

    captureAllAnswers() {
      for (let step = 1; step <= TOTAL_STEPS; step += 1) {
        this.captureStepAnswer(step);
      }
    }

    async submit() {
      if (!this.validateStep(TOTAL_STEPS)) {
        return;
      }

      this.captureAllAnswers();

      try {
        this.setLoadingState(true);
        this.analysis = await this.calculateAnalysis();

        await this.wait(LOADING_TIME_MS);

        this.renderResults(this.analysis);
        this.setLoadingState(false, true);

        if (this.emailEndpoint) {
          this.sendAnalysisByEmail().catch((error) => {
            console.error('[SOYGlow Email]', error);
            this.showPdfMessage('Tu análisis está listo para descargar, pero no hemos podido enviarlo por email.', true);
          });
        }
      } catch (error) {
        console.error('[SOYGlow Skin Analyzer]', error);
        this.setLoadingState(false);
        this.form.hidden = false;
        this.progress.hidden = false;
        this.showError(
          'No hemos podido calcular el análisis. Revisa que los archivos de datos y JavaScript estén correctamente instalados.'
        );
      }
    }

    async calculateAnalysis() {
      this.scores = this.createEmptyScores();
      const scoring = this.getScoring();

      this.applyRule(scoring.age?.[this.answers.age]);
      this.applyRule(scoring.skinFeel?.[this.answers.skinFeel]);
      this.applyRule(scoring.pores?.[this.answers.pores]);

      this.answers.concerns.forEach((concern) => {
        this.applyConcernRule(
          concern,
          scoring.concerns?.[concern]
        );
      });

      this.applyGoalRule(
        this.answers.goal,
        scoring.goals?.[this.answers.goal]
      );

      const skinTypes = this.sortScores(this.scores.skinTypes);
      const concerns = this.buildConcernRanking();
      const ingredients = this.buildIngredientRanking();
      const routineConfig = this.getRoutineConfig();
      const rankedProducts = this.rankProducts(ingredients);
      const availability = await this.getProductsAvailability(rankedProducts);
      const availableRankedProducts = rankedProducts.map((product) => ({
        ...product,
        available: availability.get(product.id) === true
      }));
      const routine = this.buildRoutine(availableRankedProducts, routineConfig);

      return {
        answers: { ...this.answers },
        scores: JSON.parse(JSON.stringify(this.scores)),
        skinTypes,
        primarySkinType: skinTypes[0] || {
          key: 'normal',
          score: 0,
          label: 'Piel normal'
        },
        secondarySkinType: skinTypes[1] || null,
        concerns,
        ingredients,
        routineConfig,
        rankedProducts: availableRankedProducts,
        routine
      };
    }

    applyRule(rule) {
      if (!rule || typeof rule !== 'object') {
        return;
      }

      this.addScoreMap('skinTypes', rule.skinTypes);
      this.addScoreMap('concerns', rule.concerns);
      this.addScoreMap('ingredients', rule.ingredients);
      this.addScoreMap('problems', rule.problems);
      this.addScoreMap('objectives', rule.objectives);
    }

    applyConcernRule(concernKey, rule) {
      if (!rule || typeof rule !== 'object') {
        if (concernKey !== 'ninguna') {
          this.addScore('concerns', concernKey, 100);
        }
        return;
      }

      this.addScore('concerns', concernKey, rule.problemScore || 100);

      if (rule.problemId) {
        this.addScore(
          'problems',
          rule.problemId,
          Number(rule.problemScore) || 100
        );
      }

      if (rule.objectiveId) {
        this.addScore(
          'objectives',
          rule.objectiveId,
          Number(rule.objectiveScore) || 60
        );
      }

      this.addScoreMap('skinTypes', rule.skinTypes);
      this.addScoreMap('ingredients', rule.ingredients);
    }

    applyGoalRule(goalKey, rule) {
      if (!rule || typeof rule !== 'object') {
        return;
      }

      if (rule.objectiveId) {
        this.addScore(
          'objectives',
          rule.objectiveId,
          Number(rule.objectiveScore) || 80
        );
      }

      this.addScore('concerns', goalKey, Number(rule.objectiveScore) || 80);
      this.addScoreMap('ingredients', rule.ingredients);
      this.addScoreMap('skinTypes', rule.skinTypes);
    }

    addScoreMap(group, map) {
      if (!map || typeof map !== 'object') {
        return;
      }

      Object.entries(map).forEach(([key, points]) => {
        this.addScore(group, key, points);
      });
    }

    addScore(group, key, points) {
      if (!key || !Number.isFinite(Number(points))) {
        return;
      }

      const normalizedKey = this.canonicalIngredientKey(key);
      const finalKey =
        group === 'ingredients' ? normalizedKey : String(key);

      this.scores[group][finalKey] =
        (this.scores[group][finalKey] || 0) + Number(points);
    }

    canonicalIngredientKey(key) {
      const normalized = this.slugify(key);
      const aliases =
        this.getKnowledge().ingredientAliases ||
        this.data.ingredientAliases ||
        {};

      return aliases[normalized] || normalized;
    }

    buildConcernRanking() {
      const selectedConcerns = this.answers.concerns
        .filter((key) => key !== 'ninguna')
        .map((key) => ({
          key,
          label: this.getConcernLabel(key),
          score: this.scores.concerns[key] || 100
        }));

      const derivedConcerns = this.sortScores(this.scores.concerns)
        .filter(
          (item) =>
            !selectedConcerns.some(
              (selected) => selected.key === item.key
            )
        )
        .map((item) => ({
          ...item,
          label: this.getConcernLabel(item.key)
        }));

      return [...selectedConcerns, ...derivedConcerns]
        .filter((item) => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 5);
    }

    buildIngredientRanking() {
      return this.sortScores(this.scores.ingredients)
        .map((item) => ({
          ...item,
          label: this.getIngredientLabel(item.key)
        }))
        .filter((item) => item.score > 0)
        .slice(0, 10);
    }

    sortScores(scoreMap) {
      return Object.entries(scoreMap || {})
        .map(([key, score]) => ({
          key,
          score: Number(score) || 0,
          label:
            scoreMap === this.scores.skinTypes
              ? this.getSkinTypeLabel(key)
              : key
        }))
        .sort((a, b) => b.score - a.score);
    }

    getRoutineConfig() {
      const scoring = this.getScoring();
      const routineLevels = scoring.routineLevels || {};
      const selected = routineLevels[this.answers.routine];

      if (selected) {
        return selected;
      }

      return {
        maxProducts: 5,
        steps: [
          'Limpieza',
          'Tónicos',
          'Serums',
          'Cremas',
          'Protector Solar'
        ]
      };
    }

    rankProducts(ingredientRanking) {
      const products = this.getProducts();
      const ingredientScores = Object.fromEntries(
        ingredientRanking.map((item) => [item.key, item.score])
      );

      const formula = this.getScoring().productFormula || {};
      const principalMultiplier =
        Number(formula.principalMultiplier) || 1;
      const inferredMultiplier =
        Number(formula.inferredEvidenceMultiplier) || 1;

      return products
        .map((product) => {
          const ingredients = Array.isArray(product.ingredients)
            ? product.ingredients
            : [];

          const matches = [];
          let score = 0;

          ingredients.forEach((relation) => {
            const rawKey =
              relation.key ||
              relation.ingredientKey ||
              relation.name ||
              relation.ingredientName;

            const ingredientKey = this.canonicalIngredientKey(rawKey);
            const ingredientScore =
              Number(ingredientScores[ingredientKey]) || 0;

            if (ingredientScore <= 0) {
              return;
            }

            const weight = Number(relation.weight) || 1;
            let contribution = ingredientScore * weight;

            if (relation.principal) {
              contribution *= principalMultiplier;
            }

            if (
              String(relation.evidence || '')
                .toLowerCase()
                .includes('inferid')
            ) {
              contribution *= inferredMultiplier;
            }

            score += contribution;

            matches.push({
              key: ingredientKey,
              label: this.getIngredientLabel(ingredientKey),
              ingredientScore,
              weight,
              contribution: Math.round(contribution)
            });
          });

          return {
            ...product,
            normalizedStep: this.normalizeStep(product.step),
            score: Math.round(score),
            matches: matches.sort(
              (a, b) => b.contribution - a.contribution
            )
          };
        })
        .sort((a, b) => {
          if (b.score !== a.score) {
            return b.score - a.score;
          }

          return String(a.name).localeCompare(String(b.name), 'es');
        });
    }

    async getProductsAvailability(products, forceRefresh = false) {
      const availability = new Map();
      const uniqueProducts = Array.from(
        new Map(
          (products || [])
            .filter((product) => product?.id && product?.handle)
            .map((product) => [product.id, product])
        ).values()
      );

      await Promise.all(
        uniqueProducts.map(async (product) => {
          const isAvailable = await this.isProductVariantAvailable(
            product,
            forceRefresh
          );
          availability.set(product.id, isAvailable);
        })
      );

      return availability;
    }

    async isProductVariantAvailable(product, forceRefresh = false) {
      const handle = String(product?.handle || '').trim();
      const variantId = Number(product?.variantId);

      if (!handle || !Number.isInteger(variantId) || variantId <= 0) {
        return false;
      }

      const cacheKey = `${handle}:${variantId}`;
      if (!forceRefresh && this.availabilityCache.has(cacheKey)) {
        return this.availabilityCache.get(cacheKey);
      }

      try {
        const rootPath = window.Shopify?.routes?.root || '/';
        const baseUrl = `${rootPath}products/${encodeURIComponent(handle)}.js`;
        const url = forceRefresh
          ? `${baseUrl}?soyglow_stock=${Date.now()}`
          : baseUrl;

        const response = await fetch(url, {
          method: 'GET',
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin',
          cache: forceRefresh ? 'no-store' : 'default'
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const productData = await response.json();
        const variants = Array.isArray(productData?.variants)
          ? productData.variants
          : [];
        const variant = variants.find(
          (item) => Number(item.id) === variantId
        );
        const isAvailable = Boolean(variant?.available);

        this.availabilityCache.set(cacheKey, isAvailable);
        return isAvailable;
      } catch (error) {
        // Si no podemos confirmar stock, no recomendamos el producto.
        console.warn(
          '[SOYGlow Stock] No se pudo comprobar disponibilidad:',
          product?.name || product?.id,
          error
        );
        this.availabilityCache.set(cacheKey, false);
        return false;
      }
    }

    async refreshRoutineAvailability(forceRefresh = true) {
      if (!this.analysis?.rankedProducts?.length) {
        return { changed: false, unavailable: [] };
      }

      const availability = await this.getProductsAvailability(
        this.analysis.rankedProducts,
        forceRefresh
      );

      const refreshedRankedProducts = this.analysis.rankedProducts.map(
        (product) => ({
          ...product,
          available: availability.get(product.id) === true
        })
      );

      const previousIds = (this.analysis.routine || []).map(
        (product) => product.id
      );
      const unavailable = (this.analysis.routine || []).filter(
        (product) => availability.get(product.id) !== true
      );
      const newRoutine = this.buildRoutine(
        refreshedRankedProducts,
        this.analysis.routineConfig
      );
      const newIds = newRoutine.map((product) => product.id);
      const changed = previousIds.join('|') !== newIds.join('|');

      this.analysis.rankedProducts = refreshedRankedProducts;
      this.analysis.routine = newRoutine;

      return { changed, unavailable };
    }

    buildRoutine(rankedProducts, routineConfig) {
      const configuredSteps = Array.isArray(routineConfig.steps)
        ? routineConfig.steps
        : DEFAULT_STEP_ORDER;

      const maxProducts =
        Number(routineConfig.maxProducts) || configuredSteps.length;

      const chosen = [];
      const usedProducts = new Set();

      configuredSteps.forEach((step) => {
        if (chosen.length >= maxProducts) {
          return;
        }

        const normalizedStep = this.normalizeStep(step);
        const candidates = rankedProducts.filter(
          (product) =>
            product.normalizedStep === normalizedStep &&
            product.available === true &&
            !usedProducts.has(product.id)
        );

        let winner = candidates.find((product) => product.score > 0);

        // Si ningún producto del paso puntúa, escoger uno como opción base.
        if (!winner && candidates.length > 0) {
          winner = candidates[0];
        }

        if (!winner) {
          return;
        }

        usedProducts.add(winner.id);
        chosen.push({
          ...winner,
          routineStep: normalizedStep,
          frequency: this.getStepFrequency(normalizedStep)
        });
      });

      return chosen;
    }

    getStepFrequency(step) {
      if (step === 'Exfoliantes') {
        return '1-2 veces por semana';
      }

      if (step === 'Mascarilla') {
        return '1-2 veces por semana';
      }

      if (step === 'Protector Solar') {
        return 'Cada mañana y reaplicar';
      }

      if (step === 'Desmaquillantes') {
        return 'Por la noche';
      }

      return 'Uso diario';
    }

    normalizeStep(step) {
      const key = this.slugify(step);
      return STEP_ALIASES[key] || String(step || '').trim();
    }

    renderResults(analysis) {
      const primary = analysis.primarySkinType;
      const secondary = analysis.secondarySkinType;

      if (this.resultTitle) {
        this.resultTitle.textContent =
          `Tu perfil: ${primary.label}`;
      }

      if (this.resultSummary) {
        this.resultSummary.textContent =
          this.buildSummary(analysis);
      }

      if (this.skinProfileElement) {
        const secondaryText =
          secondary && secondary.score > 0
            ? `<p><strong>Rasgo secundario:</strong> ${this.escapeHtml(
                secondary.label
              )}</p>`
            : '';

        this.skinProfileElement.innerHTML = `
          <p class="soyglow-analyzer__profile-name">
            <strong>${this.escapeHtml(primary.label)}</strong>
          </p>
          ${secondaryText}
          <p>${this.escapeHtml(
            this.getSkinTypeDescription(primary.key)
          )}</p>
        `;
      }

      if (this.concernsElement) {
        this.concernsElement.innerHTML =
          analysis.concerns.length > 0
            ? this.renderList(
                analysis.concerns.map((item) => item.label)
              )
            : '<p>Tu piel parece equilibrada. Nos centraremos en mantenerla protegida e hidratada.</p>';
      }

      if (this.ingredientsElement) {
        this.ingredientsElement.innerHTML = this.renderIngredientRanking(
          analysis.ingredients.slice(0, 5)
        );
      }

      if (this.routineElement) {
        this.routineElement.innerHTML = this.renderRoutine(
          analysis.routine
        );
      }

      if (this.explanationElement) {
        this.explanationElement.textContent =
          this.buildExplanation(analysis);
      }
    }

    renderList(items) {
      const cleanItems = items.filter(Boolean);

      if (cleanItems.length === 0) {
        return '<p>No se han detectado necesidades prioritarias.</p>';
      }

      return `
        <ul class="soyglow-analyzer__result-list">
          ${cleanItems
            .map(
              (item) =>
                `<li>${this.escapeHtml(item)}</li>`
            )
            .join('')}
        </ul>
      `;
    }

    renderIngredientRanking(ingredients) {
      if (!ingredients.length) {
        return '<p>Hidratación, protección y equilibrio.</p>';
      }

      return `
        <ol class="soyglow-analyzer__ingredient-ranking">
          ${ingredients
            .map(
              (item) => `
                <li>
                  <strong>${this.escapeHtml(item.label)}</strong>
                </li>
              `
            )
            .join('')}
        </ol>
      `;
    }

    renderRoutine(routine) {
      if (!routine.length) {
        return `
          <p>
            No hemos encontrado suficientes productos relacionados.
            Comprueba que las relaciones producto-ingrediente estén incluidas
            en skin-analyzer-data.js.
          </p>
        `;
      }

      return routine
        .map((product, index) => {
          const url =
            product.url ||
            (product.handle
              ? `/products/${encodeURIComponent(product.handle)}`
              : '#');

          const topMatches = (product.matches || [])
            .slice(0, 3)
            .map((match) => match.label)
            .join(', ');

          return `
            <article class="soyglow-analyzer__routine-item">
              <div class="soyglow-analyzer__routine-copy">
                <p class="soyglow-analyzer__routine-step">
                  Paso ${index + 1} · ${this.escapeHtml(product.routineStep)}
                </p>
                <h4>${this.escapeHtml(product.name || 'Producto SOYGlow')}</h4>
                ${
                  product.brand
                    ? `<p>${this.escapeHtml(product.brand)}</p>`
                    : ''
                }
                ${
                  topMatches
                    ? `<p><strong>Ingredientes destacados:</strong> ${this.escapeHtml(
                        topMatches
                      )}</p>`
                    : ''
                }
                <small>${this.escapeHtml(product.frequency)}</small>
              </div>

              <a
                class="soyglow-analyzer__button soyglow-analyzer__button--primary"
                href="${this.escapeAttribute(url)}"
              >
                Ver producto
              </a>
            </article>
          `;
        })
        .join('');
    }

    buildSummary(analysis) {
      const concerns = analysis.concerns
        .slice(0, 3)
        .map((item) => item.label.toLowerCase());

      if (concerns.length === 0) {
        return (
          'Tu piel se percibe equilibrada. Hemos preparado una rutina sencilla ' +
          'para mantener la hidratación, la luminosidad y la protección diaria.'
        );
      }

      return (
        `Según tus respuestas, tu piel se identifica principalmente como ` +
        `${analysis.primarySkinType.label.toLowerCase()} y necesita priorizar ` +
        `${this.joinSpanish(concerns)}.`
      );
    }

    buildExplanation(analysis) {
      const ingredients = analysis.ingredients
        .slice(0, 4)
        .map((item) => item.label);

      const concerns = analysis.concerns
        .slice(0, 3)
        .map((item) => item.label.toLowerCase());

      const ingredientText =
        ingredients.length > 0
          ? this.joinSpanish(ingredients)
          : 'ingredientes hidratantes, calmantes y antioxidantes';

      const concernText =
        concerns.length > 0
          ? this.joinSpanish(concerns)
          : 'mantener el equilibrio de la piel';

      return (
        `Hemos priorizado ${concernText}. Por eso hemos buscado productos ` +
        `de SOYGlow que contienen ${ingredientText}. La rutina se ha adaptado ` +
        `también al nivel de experiencia que indicaste para que sea práctica, ` +
        `progresiva y fácil de mantener. El resultado es orientativo y no ` +
        `sustituye el diagnóstico de un profesional sanitario.`
      );
    }

    getSkinTypeLabel(key) {
      const labels =
        this.getConfig().labels?.skinTypes ||
        this.data.labels?.skinTypes ||
        FALLBACK_SKIN_LABELS;

      return labels[key] || FALLBACK_SKIN_LABELS[key] || this.humanize(key);
    }

    getSkinTypeDescription(key) {
      const knowledge = this.getKnowledge();
      const skinType =
        knowledge.skinTypesByKey?.[key] ||
        (Array.isArray(knowledge.skinTypes)
          ? knowledge.skinTypes.find((item) => item.key === key)
          : null);

      if (skinType?.description) {
        return skinType.description;
      }

      const fallback = {
        grasa:
          'Produce más sebo y puede presentar brillos, poros visibles o tendencia a brotes.',
        mixta:
          'Combina zonas con más grasa, especialmente la zona T, con otras normales o secas.',
        normal:
          'Presenta un equilibrio general entre sebo, hidratación y sensibilidad.',
        seca:
          'Produce menos sebo y suele necesitar más nutrición y apoyo para la barrera cutánea.',
        deshidratada:
          'Le falta agua y puede sentirse tirante aunque también produzca grasa.'
      };

      return fallback[key] || 'Perfil de piel calculado según tus respuestas.';
    }

    getConcernLabel(key) {
      const scoringConcern = this.getScoring().concerns?.[key];
      const problemId = scoringConcern?.problemId;
      const knowledge = this.getKnowledge();

      const problem =
        problemId &&
        (knowledge.problemsById?.[problemId] ||
          (Array.isArray(knowledge.problems)
            ? knowledge.problems.find((item) => item.id === problemId)
            : null));

      return (
        problem?.name ||
        FALLBACK_CONCERN_LABELS[key] ||
        this.humanize(key)
      );
    }

    getIngredientLabel(key) {
      const knowledge = this.getKnowledge();

      const ingredient =
        knowledge.ingredientsByKey?.[key] ||
        (Array.isArray(knowledge.ingredients)
          ? knowledge.ingredients.find((item) => item.key === key)
          : null) ||
        knowledge.externalIngredients?.[key];

      if (ingredient?.name) {
        return ingredient.name;
      }

      const legacyIngredients = this.data.ingredients;
      if (legacyIngredients && legacyIngredients[key]?.name) {
        return legacyIngredients[key].name;
      }

      return this.humanize(key);
    }

    setLoadingState(isLoading, showResults = false) {
      if (isLoading) {
        this.form.hidden = true;
        this.progress.hidden = true;
        this.resultsSection.hidden = true;
        this.loadingSection.hidden = false;
        this.loadingSection.setAttribute('aria-busy', 'true');
        this.scrollToAnalyzer();
        return;
      }

      this.loadingSection.hidden = true;
      this.loadingSection.setAttribute('aria-busy', 'false');

      if (showResults) {
        this.form.hidden = true;
        this.progress.hidden = true;
        this.resultsSection.hidden = false;
        this.scrollToAnalyzer();
      }
    }


    createPdfDocument() {
      const jsPDF = window.jspdf?.jsPDF;

      if (!jsPDF || !this.analysis) {
        throw new Error('El generador de PDF no está disponible.');
      }

      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const margin = 18;
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const maxWidth = pageWidth - (margin * 2);
      let y = 22;

      const ensureSpace = (needed = 12) => {
        if (y + needed > pageHeight - 18) {
          doc.addPage();
          y = 20;
        }
      };

      const addWrapped = (text, size = 10, gap = 5, style = 'normal') => {
        ensureSpace(14);
        doc.setFont('helvetica', style);
        doc.setFontSize(size);
        const lines = doc.splitTextToSize(String(text || ''), maxWidth);
        doc.text(lines, margin, y);
        y += (lines.length * (size * 0.42)) + gap;
      };

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(21);
      doc.text('SOYGlow', margin, y);
      y += 9;
      doc.setFontSize(16);
      doc.text('Tu análisis de piel', margin, y);
      y += 8;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Skin Analyzer by SOYGlow · ${new Date().toLocaleDateString('es-ES')}`, margin, y);
      y += 12;

      addWrapped(this.resultTitle?.textContent || `Tu perfil: ${this.analysis.primarySkinType.label}`, 14, 6, 'bold');
      addWrapped(this.buildSummary(this.analysis), 10, 10);

      addWrapped('TIPO DE PIEL', 11, 4, 'bold');
      addWrapped(this.analysis.primarySkinType.label, 12, 4, 'bold');
      addWrapped(this.getSkinTypeDescription(this.analysis.primarySkinType.key), 10, 8);

      addWrapped('NECESIDADES DETECTADAS', 11, 4, 'bold');
      const concerns = this.analysis.concerns.length
        ? this.analysis.concerns.slice(0, 5).map((item) => `• ${item.label}`).join('\n')
        : '• Mantener hidratación, equilibrio y protección diaria';
      addWrapped(concerns, 10, 8);

      addWrapped('INGREDIENTES RECOMENDADOS', 11, 4, 'bold');
      const ingredients = this.analysis.ingredients.slice(0, 5).map((item) => `• ${item.label}`).join('\n');
      addWrapped(ingredients || '• Hidratación, protección y equilibrio', 10, 10);

      addWrapped('TU RUTINA PERSONALIZADA', 13, 6, 'bold');
      this.analysis.routine.forEach((product, index) => {
        ensureSpace(30);
        addWrapped(`Paso ${index + 1} · ${product.routineStep}`, 10, 2, 'bold');
        addWrapped(`${product.brand ? product.brand + ' · ' : ''}${product.name}`, 11, 2, 'bold');
        const topMatches = (product.matches || []).slice(0, 3).map((match) => match.label).join(', ');
        if (topMatches) addWrapped(`Ingredientes destacados: ${topMatches}`, 9, 2);
        if (product.frequency) addWrapped(`Frecuencia: ${product.frequency}`, 9, 3);
        if (product.url) addWrapped(product.url, 8, 6);
      });

      addWrapped('¿POR QUÉ ESTA RUTINA?', 11, 4, 'bold');
      addWrapped(this.buildExplanation(this.analysis), 9, 8);
      addWrapped('Piel sana, belleza auténtica. · www.soyglow.es', 9, 2, 'bold');
      addWrapped('Este resultado es orientativo y no sustituye el diagnóstico de un profesional sanitario.', 8, 0);

      return doc;
    }

    downloadAnalysisPdf() {
      try {
        const doc = this.createPdfDocument();
        const safeProfile = this.slugify(this.analysis?.primarySkinType?.label || 'analisis');
        doc.save(`SOYGlow-analisis-${safeProfile}.pdf`);
        this.showPdfMessage('PDF generado correctamente.', false);
      } catch (error) {
        console.error('[SOYGlow PDF]', error);
        this.showPdfMessage(error.message || 'No se pudo generar el PDF.', true);
      }
    }

    async sendAnalysisByEmail() {
      if (!this.emailEndpoint || !this.user.email || !this.analysis) return;

      const doc = this.createPdfDocument();
      const pdfDataUri = doc.output('datauristring');

      const payload = {
        email: this.user.email,
        marketingConsent: this.user.marketingConsent,
        generatedAt: new Date().toISOString(),
        profile: this.analysis.primarySkinType.label,
        summary: this.buildSummary(this.analysis),
        concerns: this.analysis.concerns.slice(0, 5).map((item) => item.label),
        ingredients: this.analysis.ingredients.slice(0, 5).map((item) => item.label),
        routine: this.analysis.routine.map((product, index) => ({
          step: index + 1,
          routineStep: product.routineStep,
          name: product.name,
          brand: product.brand || '',
          frequency: product.frequency || '',
          url: product.url || ''
        })),
        pdfDataUri
      };

      const response = await fetch(this.emailEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('No se pudo enviar el análisis por email.');
      }

      this.showPdfMessage(`También hemos enviado una copia a ${this.user.email}.`, false);
    }

    showPdfMessage(message, isError = false) {
      if (!this.pdfMessage) return;
      this.pdfMessage.textContent = message;
      this.pdfMessage.hidden = false;
      this.pdfMessage.classList.toggle('is-error', isError);
      this.pdfMessage.classList.toggle('is-success', !isError);
    }

    hidePdfMessage() {
      if (!this.pdfMessage) return;
      this.pdfMessage.hidden = true;
      this.pdfMessage.textContent = '';
      this.pdfMessage.classList.remove('is-error', 'is-success');
    }

    async addRoutineToCart() {
      if (!this.analysis?.routine?.length) {
        this.showCartMessage(
          'No hay productos disponibles para añadir.',
          true
        );
        return;
      }

      const uniqueVariants = new Map();

      this.analysis.routine.forEach((product) => {
        const variantId = Number(product.variantId);

        if (!Number.isInteger(variantId) || variantId <= 0) {
          return;
        }

        if (!uniqueVariants.has(variantId)) {
          uniqueVariants.set(variantId, {
            id: variantId,
            quantity: 1,
            properties: {
              '_Origen': 'Skin Analyzer by SOYGlow',
              '_Paso de rutina': product.routineStep
            }
          });
        }
      });

      const items = Array.from(uniqueVariants.values());

      if (items.length === 0) {
        this.showCartMessage(
          'Los productos recomendados no tienen un ID de variante válido.',
          true
        );
        return;
      }

      const missingProducts = this.analysis.routine.filter((product) => {
        const variantId = Number(product.variantId);
        return !Number.isInteger(variantId) || variantId <= 0;
      });

      this.addRoutineButton.disabled = true;
      this.addRoutineButton.textContent = 'Añadiendo rutina...';
      this.hideCartMessage();

      try {
        this.addRoutineButton.textContent = 'Comprobando stock...';
        const stockCheck = await this.refreshRoutineAvailability(true);

        if (!this.analysis?.routine?.length) {
          throw new Error(
            'Ahora mismo no tenemos disponibles los productos necesarios para completar esta rutina.'
          );
        }

        if (stockCheck.changed) {
          this.renderResults(this.analysis);
          this.showCartMessage(
            'Hemos actualizado tu rutina porque uno de los productos ya no estaba disponible.',
            false
          );
        }

        const refreshedVariants = new Map();
        this.analysis.routine.forEach((product) => {
          const variantId = Number(product.variantId);
          if (Number.isInteger(variantId) && variantId > 0 && !refreshedVariants.has(variantId)) {
            refreshedVariants.set(variantId, {
              id: variantId,
              quantity: 1,
              properties: {
                '_Origen': 'Skin Analyzer by SOYGlow',
                '_Paso de rutina': product.routineStep
              }
            });
          }
        });
        const finalItems = Array.from(refreshedVariants.values());

        if (finalItems.length === 0) {
          throw new Error('No hay productos disponibles para añadir a la cesta.');
        }

        this.addRoutineButton.textContent = 'Añadiendo rutina...';
        const rootPath = window.Shopify?.routes?.root || '/';

        const response = await fetch(`${rootPath}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ items: finalItems })
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'No se pudo añadir la rutina.'
          );
        }

        const extraMessage = missingProducts.length
          ? ` ${missingProducts.length} producto(s) no se añadieron porque no tenían variante.`
          : '';

        this.showCartMessage(
          `${finalItems.length} productos añadidos a tu cesta.${extraMessage}`,
          false
        );

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: result
          })
        );

        window.setTimeout(() => {
          window.location.href = `${rootPath}cart`;
        }, 850);
      } catch (error) {
        console.error('[SOYGlow Cart]', error);

        this.showCartMessage(
          error.message || 'No se pudo añadir la rutina a la cesta.',
          true
        );
      } finally {
        this.addRoutineButton.disabled = false;
        this.addRoutineButton.textContent =
          'Añadir toda mi rutina a la cesta';
      }
    }

    showCartMessage(message, isError = false) {
      if (!this.cartMessage) {
        return;
      }

      this.cartMessage.textContent = message;
      this.cartMessage.hidden = false;
      this.cartMessage.classList.toggle('is-error', isError);
      this.cartMessage.classList.toggle('is-success', !isError);
    }

    hideCartMessage() {
      if (!this.cartMessage) {
        return;
      }

      this.cartMessage.hidden = true;
      this.cartMessage.textContent = '';
      this.cartMessage.classList.remove('is-error', 'is-success');
    }

    restart() {
      this.form.reset();
      this.answers = this.createEmptyAnswers();
      this.scores = this.createEmptyScores();
      this.analysis = null;
      this.user = { email: '', marketingConsent: false };
      this.hideCartMessage();
      this.hidePdfMessage();

      if (this.emailInput) this.emailInput.value = '';
      if (this.marketingConsentInput) this.marketingConsentInput.checked = false;
      if (this.emailGate) this.emailGate.hidden = false;
      this.resultsSection.hidden = true;
      this.loadingSection.hidden = true;
      this.form.hidden = true;
      this.progress.hidden = true;
      this.currentStep = 1;
      this.steps.forEach((step) => {
        const isFirst = Number(step.dataset.step) === 1;
        step.hidden = !isFirst;
        step.classList.toggle('is-active', isFirst);
      });
      this.scrollToAnalyzer();
    }

    showError(message) {
      if (!this.errorElement) {
        return;
      }

      this.errorElement.textContent = message;
      this.errorElement.hidden = false;
    }

    clearError() {
      if (!this.errorElement) {
        return;
      }

      this.errorElement.textContent = '';
      this.errorElement.hidden = true;
    }

    scrollToAnalyzer() {
      window.requestAnimationFrame(() => {
        const top =
          this.root.getBoundingClientRect().top +
          window.scrollY -
          24;

        window.scrollTo({
          top: Math.max(top, 0),
          behavior: 'smooth'
        });
      });
    }

    wait(milliseconds) {
      return new Promise((resolve) =>
        window.setTimeout(resolve, milliseconds)
      );
    }

    slugify(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    }

    humanize(value) {
      const text = String(value || '')
        .replace(/_/g, ' ')
        .trim();

      return text.charAt(0).toUpperCase() + text.slice(1);
    }

    joinSpanish(items) {
      const cleanItems = items.filter(Boolean);

      if (cleanItems.length === 0) {
        return '';
      }

      if (cleanItems.length === 1) {
        return cleanItems[0];
      }

      return (
        cleanItems.slice(0, -1).join(', ') +
        ' y ' +
        cleanItems[cleanItems.length - 1]
      );
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = String(value ?? '');
      return div.innerHTML;
    }

    escapeAttribute(value) {
      return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/"/g, '&quot;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    }
  }

  function boot() {
    const root = document.getElementById(ROOT_ID);

    if (!root || root.dataset.initialized === 'true') {
      return;
    }

    root.dataset.initialized = 'true';

    try {
      const analyzer = new SkinAnalyzer(root);

      window.SOYGlowSkinAnalyzer =
        window.SOYGlowSkinAnalyzer || {};

      window.SOYGlowSkinAnalyzer.app = analyzer;
    } catch (error) {
      console.error('[SOYGlow Skin Analyzer]', error);

      const errorElement = root.querySelector('#soyglow-form-error');
      if (errorElement) {
        errorElement.hidden = false;
        errorElement.textContent =
          'No se ha podido iniciar el Skin Analyzer. Comprueba que skin-analyzer-data.js se carga antes de skin-analyzer.js.';
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Shopify vuelve a cargar secciones en el editor de temas.
  document.addEventListener('shopify:section:load', boot);
})(window, document);
